"use client";

import { Card } from "@/components/ui/card";
import { Button } from "../ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { ArrowUp } from "lucide-react";
import { PromptProps } from "./prompt-types";
import { useState, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const segmentBadgeVariants = {
  initial: { rotate: 12 },
  animate: {
    rotate: [12, 14, 10, 12],
    x: [0, -1.5, 1.5, 0],
    y: [0, -0.5, 0.5, 0],
    transition: {
      duration: 0.6,
      ease: "easeInOut",
      repeat: Infinity,
      repeatDelay: 10,
    },
  },
  exit: {
    scale: 1.35,
    opacity: 0,
    filter: "blur(3px)",
    rotate: 0,
    transition: { duration: 0.35, ease: "anticipate" },
  },
};
import { Capability, Segment } from "./prompt-types";
import posthog from "posthog-js";
import { callInference } from "@/lib/playground-utils";
import { Spinner } from "../ui/spinner";
import PromptText from "./prompt-text";
import PromptCaption from "./prompt-caption";
import SuggestedQueries from "./suggested-queries";
import { streamCaptionInference } from "./caption-streaming";
import { streamSegmentationInference } from "./segmentation-streaming";
import { streamSegmentationMultiInference } from "./segmentation-multi-streaming";

// Import the PromptOptions interface from prompt-caption.tsx
import type { PromptOptions } from "./prompt-caption";
import { CaptionLength } from "./prompt-caption";

function isInferButtonEnabled(
  imagesLoaded: boolean,
  prompt: string,
  needPrompt: boolean
) {
  if (!imagesLoaded) return false;
  if (needPrompt && prompt.trim() == "") return false;
  return true;
}

export default function Prompt(promptProps: PromptProps) {
  const [prompt, setPrompt] = useState("");
  const [promptOptions, setPromptOptions] = useState<PromptOptions>({
    length: CaptionLength.Normal,
  });
  const [capability, setCapability] = useState<Capability>(Capability.VisualQuery);
  const [inferring, setInferring] = useState(false);
  const [needPrompt, setNeedPrompt] = useState(true);
  const [reasoningEnabled, setReasoningEnabled] = useState(true);
  const [pointSegmentMulti, setPointSegmentMulti] = useState(true); // true = many objects (point+segment), false = single object (segment only)
  const [showSegmentBadge, setShowSegmentBadge] = useState(true);

  // Set initial values from props when they become available
  useEffect(() => {
    if (promptProps.initialPrompt) {
      setPrompt(promptProps.initialPrompt);
    }
  }, [promptProps.initialPrompt]);

  useEffect(() => {
    if (promptProps.initialSkill) {
      const skillMap: Record<string, Capability> = {
        'query': Capability.VisualQuery,
        'caption': Capability.Caption,
        'detect': Capability.ObjectDetection,
        'point': Capability.Pointing,
        'point-segment': Segment,
        'segment': Segment,
      };
      const mappedSkill = skillMap[promptProps.initialSkill];
      if (mappedSkill) {
        setCapability(mappedSkill);
        setNeedPrompt(mappedSkill !== Capability.Caption);
      }
    }
  }, [promptProps.initialSkill]);

  // Submit inference request
  const handleInfer = useCallback(
    async (query: string) => {
      const {
        imageFile,
        uploadedImage,
        setInferences,
        inferenceUrl,
        variant,
      } = promptProps;

      if (!imageFile || !uploadedImage) return;

      setInferring(true);

      if (capability === Capability.Caption) {
        try {
          await streamCaptionInference({
            inferenceUrl,
            query,
            imageFile,
            uploadedImage,
            promptOptions,
            reasoningEnabled,
            variant,
            setInferences,
          });
        } catch (err) {
          posthog.captureException(err);
        } finally {
          setInferring(false);
        }
        return;
      }

      if (capability === Segment) {
        try {
          if (pointSegmentMulti) {
            await streamSegmentationMultiInference({
              inferenceUrl,
              query,
              imageFile,
              uploadedImage,
              promptOptions,
              reasoningEnabled,
              variant,
              setInferences,
            });
          } else {
            await streamSegmentationInference({
              inferenceUrl,
              query,
              imageFile,
              uploadedImage,
              promptOptions,
              reasoningEnabled,
              variant,
              setInferences,
              capability: Capability.Segmentation,
            });
          }
        } catch (err) {
          posthog.captureException(err);
        } finally {
          setInferring(false);
        }
        return;
      }

      try {
        const newInference = await callInference(
          inferenceUrl,
          capability,
          query,
          imageFile,
          uploadedImage,
          { ...promptOptions, reasoning: reasoningEnabled },
          variant
        );
        newInference.captionLength = promptOptions.length || "Normal";

        setInferences((prev) => [newInference, ...prev]);
      } catch (err) {
        posthog.captureException(err);
      } finally {
        setInferring(false);
      }
    },
    [promptProps, capability, promptOptions, reasoningEnabled, pointSegmentMulti]
  );



  const onTabChange = (value: Capability) => {
    setCapability(value);
    setPromptOptions({ length: CaptionLength.Normal });
    setNeedPrompt(value !== Capability.Caption);
    if (value !== Segment) {
      setPointSegmentMulti(true);
    }
    if (value === Segment && showSegmentBadge) {
      setShowSegmentBadge(false);
    }
  };

  const inferEnabled = isInferButtonEnabled(
    !!promptProps.imageFile,
    prompt,
    needPrompt
  );

  function handleSuggestedQuerySelect(query: string) {
    setPrompt(query);
    handleInfer(query);
  }

  return (
    <Card className="px-4 pt-4 pb-2 h-full">
      <Tabs
        className="h-full"
        value={capability as string}
        onValueChange={(value) => onTabChange(value as Capability)}
      >
        <div className="flex flex-col h-full">
          <TabsContent value={Capability.VisualQuery as string}>
            <PromptText
              prompt={prompt}
              setPrompt={setPrompt}
              handleSubmit={(query) => handleInfer(query)}
              placeholder="Enter a question..."
            />
          </TabsContent>
          <TabsContent value={Capability.Caption as string}>
            <PromptCaption
              setPromptOptions={setPromptOptions}
              promptOptions={promptOptions}
            />
          </TabsContent>
          <TabsContent value={Capability.Pointing as string}>
            <PromptText
              prompt={prompt}
              setPrompt={setPrompt}
              handleSubmit={(query) => handleInfer(query)}
              placeholder="Enter something to point at..."
            />
          </TabsContent>
          <TabsContent value={Capability.ObjectDetection as string}>
            <PromptText
              prompt={prompt}
              setPrompt={setPrompt}
              handleSubmit={(query) => handleInfer(query)}
              placeholder="Enter objects to detect..."
            />
          </TabsContent>
          <TabsContent value={Segment}>
            <PromptText
              prompt={prompt}
              setPrompt={setPrompt}
              handleSubmit={(query) => handleInfer(query)}
              placeholder="Describe what to segment..."
            />
          </TabsContent>
        </div>
        <div>
          {promptProps.imageFile && needPrompt && (
            <SuggestedQueries
              inferenceUrl={promptProps.inferenceUrl}
              imageData={promptProps.imageFile}
              capability={capability}
              selectSuggestedQuery={handleSuggestedQuerySelect}
            />
          )}
        </div>

        {/* Reasoning toggle - shown above tabs on mobile, inline on desktop */}
        {capability === Capability.VisualQuery && (
          <div className="flex items-center space-x-2 mb-1 lg:hidden">
            <Switch
              id="reasoning-toggle-mobile"
              checked={reasoningEnabled}
              onCheckedChange={setReasoningEnabled}
            />
            <Label
              htmlFor="reasoning-toggle-mobile"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              Reasoning
            </Label>
          </div>
        )}

        {capability === Segment && (
          <div className="flex items-center space-x-2 mb-1 lg:hidden">
            <Switch
              id="point-segment-mode-mobile"
              checked={pointSegmentMulti}
              onCheckedChange={setPointSegmentMulti}
            />
            <Label
              htmlFor="point-segment-mode-mobile"
              className="text-sm text-muted-foreground cursor-pointer"
            >
              {pointSegmentMulti ? "Many Objects" : "Single Object"}
            </Label>
          </div>
        )}

        <div className="flex justify-between items-center flex-wrap gap-3">
          <div className="flex items-center space-x-4 flex-wrap gap-3">
            <TabsList>
              <TabsTrigger value={Capability.VisualQuery}>Query</TabsTrigger>
              <TabsTrigger value={Capability.Caption}>Caption</TabsTrigger>
              <TabsTrigger value={Capability.Pointing}>Point</TabsTrigger>
              <TabsTrigger value={Capability.ObjectDetection}>Detect</TabsTrigger>
              <TabsTrigger
                value={Segment}
                className="relative overflow-visible pr-3"
              >
                Segment
                <AnimatePresence>
                  {showSegmentBadge && (
                    <motion.span
                      key="segment-new-badge"
                      className="pointer-events-none absolute -right-2 -top-2 rounded-sm bg-primary px-[6px] py-[2px] text-[10px] font-semibold uppercase tracking-tight text-primary-foreground shadow-sm"
                      variants={segmentBadgeVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                    >
                      New
                    </motion.span>
                  )}
                </AnimatePresence>
              </TabsTrigger>
            </TabsList>

            {/* Reasoning toggle - shown inline on desktop */}
            {capability === Capability.VisualQuery && (
              <div className="hidden lg:flex items-center space-x-2">
                <Switch
                  id="reasoning-toggle-desktop"
                  checked={reasoningEnabled}
                  onCheckedChange={setReasoningEnabled}
                />
                <Label
                  htmlFor="reasoning-toggle-desktop"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  Reasoning
                </Label>
              </div>
            )}

            {/* Segment mode toggle */}
            {capability === Segment && (
              <div className="hidden lg:flex items-center space-x-2">
                <Switch
                  id="point-segment-mode-desktop"
                  checked={pointSegmentMulti}
                  onCheckedChange={setPointSegmentMulti}
                />
                <Label
                  htmlFor="point-segment-mode-desktop"
                  className="text-sm text-muted-foreground cursor-pointer"
                >
                  {pointSegmentMulti ? "Many Objects" : "Single Object"}
                </Label>
              </div>
            )}
          </div>

          <Button disabled={!inferEnabled} onClick={() => handleInfer(prompt)}>
            {inferring ? (
              <Spinner size="small" className="stroke-primary-foreground" />
            ) : (
              <ArrowUp />
            )}
          </Button>
        </div>
      </Tabs>
    </Card>
  );
}
